/**
 * Sensor config validation module — mirrors firmware `cfgSetDefaultCfg`,
 * `cfgValidateSensorCfg`, and `cfgSwitchSensorCfg` from `global.cpp`.
 *
 * All functions are pure (no Vue/component dependency) so they can be
 * unit-tested independently.
 */

import { ComBoxSensorUsage } from "@/data/models/ControlConfig";
import {
  ComBoxSensorType,
  ROOM_SENSOR_INDEX,
} from "@/data/models/ComBoxSensorData";
import { getSensorSuffix } from "@/data/models/ComBox";

// ---------------------------------------------------------------------------
// Program rules table — TypeScript equivalent of firmware `prgRules[]`
// ---------------------------------------------------------------------------

/** Per-program sensor assignment and validation rules. */
export interface ProgramSensorRules {
  roomDefault: ComBoxSensorUsage;
  extDefault: ComBoxSensorUsage;
  roomCanBeRef: boolean;
  extCanBeRef: boolean;
  autoCreateRef: boolean;
  autoCreateProg: boolean;
  roomIsDefaultRef: boolean;
  /** P2 special: auto-assign 1st ext=REF, 2nd ext=PROG when ≥2 usable ext */
  autoAssignOnSecondExt: boolean;
}

/**
 * Program sensor rules indexed by program number (P0–P5).
 * Matches firmware `prgRules[]` exactly.
 */
export const PROGRAM_SENSOR_RULES: ProgramSensorRules[] = [
  // P0 — Unterdruck
  {
    roomDefault: ComBoxSensorUsage.REFERENCE,
    extDefault: ComBoxSensorUsage.PROGRAM,
    roomCanBeRef: true,
    extCanBeRef: true,
    autoCreateRef: true,
    autoCreateProg: true,
    roomIsDefaultRef: true,
    autoAssignOnSecondExt: false,
  },
  // P1 — Schiebezug
  {
    roomDefault: ComBoxSensorUsage.REFERENCE,
    extDefault: ComBoxSensorUsage.PROGRAM,
    roomCanBeRef: true,
    extCanBeRef: true,
    autoCreateRef: true,
    autoCreateProg: true,
    roomIsDefaultRef: true,
    autoAssignOnSecondExt: false,
  },
  // P2 — Überdruck
  {
    roomDefault: ComBoxSensorUsage.MEASUREMENT,
    extDefault: ComBoxSensorUsage.PROGRAM,
    roomCanBeRef: true,
    extCanBeRef: true,
    autoCreateRef: true,
    autoCreateProg: true,
    roomIsDefaultRef: true,
    autoAssignOnSecondExt: true,
  },
  // P3 — Monitor
  {
    roomDefault: ComBoxSensorUsage.MEASUREMENT,
    extDefault: ComBoxSensorUsage.MEASUREMENT,
    roomCanBeRef: false,
    extCanBeRef: false,
    autoCreateRef: false,
    autoCreateProg: false,
    roomIsDefaultRef: false,
    autoAssignOnSecondExt: false,
  },
  // P4 — Raumtrocknung
  {
    roomDefault: ComBoxSensorUsage.PROGRAM,
    extDefault: ComBoxSensorUsage.MEASUREMENT,
    roomCanBeRef: false,
    extCanBeRef: false,
    autoCreateRef: false,
    autoCreateProg: false,
    roomIsDefaultRef: false,
    autoAssignOnSecondExt: false,
  },
  // P5 — Zelttrocknung / Target Drying
  {
    roomDefault: ComBoxSensorUsage.PROGRAM,
    extDefault: ComBoxSensorUsage.PROGRAM,
    roomCanBeRef: false,
    extCanBeRef: false,
    autoCreateRef: false,
    autoCreateProg: true,
    roomIsDefaultRef: false,
    autoAssignOnSecondExt: false,
  },
];

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/** Returns true if a sensor slot is connected (not EMPTY/UNKNOWN). */
function isSlotConnected(sensorType: ComBoxSensorType): boolean {
  return (
    sensorType !== ComBoxSensorType.EMPTY &&
    sensorType !== ComBoxSensorType.UNKNOWN
  );
}

/**
 * Returns true when the slot holds a sensor that is locked to MEASUREMENT:
 * BLE type 3 (material) and BLE type 5 sensors.
 */
function isMeasurementOnlySensor(sensorType: ComBoxSensorType): boolean {
  return (
    sensorType === ComBoxSensorType.BLE_SENSOR_TYPE_3 ||
    sensorType === ComBoxSensorType.BLE_SENSOR_TYPE_MATERIAL ||
    sensorType === ComBoxSensorType.BLE_SENSOR_TYPE_5
  );
}

/**
 * Enforces product rule: BLE type 3 and type 5 sensors are always MEASUREMENT.
 * This applies regardless of selected program or reference-sensor rules.
 */
function enforceMeasurementOnlySensors(
  usage: ComBoxSensorUsage[],
  sensorTypes: ComBoxSensorType[],
): void {
  for (let i = 0; i < usage.length; i++) {
    if (isMeasurementOnlySensor(sensorTypes[i]) && isSlotConnected(sensorTypes[i])) {
      usage[i] = ComBoxSensorUsage.MEASUREMENT;
    }
  }
}

/**
 * Counts connected external sensors (excluding room sensor at index 4).
 * "Connected" means the slot is not EMPTY and not UNKNOWN.
 */
export function countUsableExtSensors(sensorTypes: ComBoxSensorType[]): number {
  let count = 0;
  for (let i = 0; i < sensorTypes.length; i++) {
    if (i === ROOM_SENSOR_INDEX) continue;
    if (isSlotConnected(sensorTypes[i])) count++;
  }
  return count;
}

/**
 * Returns true if the sensor at the given slot is a "short" BLE sensor.
 * Short sensors have serial prefix "WSB" (suffix "S"). Long sensors have "WLB" (suffix "L").
 * Non-BLE sensors are always considered "long" (not restricted).
 *
 * If no serial number is available and the sensor is BLE_SENSOR_TYPE_1,
 * it defaults based on device type: MINI → short, BOX → long.
 */
export function isShortSensor(
  sensorType: ComBoxSensorType,
  serialNumber?: string,
  isMini?: boolean,
): boolean {
  return getSensorSuffix(sensorType, serialNumber, isMini) === "S";
}

/**
 * Returns true if the short sensor restriction applies for the given program
 * and room usage. The rule is: P0/P1/P2 when room=REF.
 */
function isShortSensorRestrictionActive(
  programNumber: number,
  roomUsage: ComBoxSensorUsage,
): boolean {
  return (
    (programNumber === 0 || programNumber === 1 || programNumber === 2) &&
    roomUsage === ComBoxSensorUsage.REFERENCE
  );
}

/**
 * Returns the program rules for the given program number.
 * Falls back to P0 rules if the program number is out of range.
 */
function getRules(programNumber: number): ProgramSensorRules {
  if (programNumber >= 0 && programNumber < PROGRAM_SENSOR_RULES.length) {
    return PROGRAM_SENSOR_RULES[programNumber];
  }
  return PROGRAM_SENSOR_RULES[0];
}

// ---------------------------------------------------------------------------
// getDefaultSensorConfig — mirrors firmware cfgSetDefaultCfg()
// ---------------------------------------------------------------------------

/**
 * Computes the default sensor configuration for a given program,
 * based on the program rules and currently connected sensor hardware.
 *
 * Called on:
 * - START dialog open (always — replaces client config sensor usage)
 * - Program change in dialog (both START and EDIT)
 *
 * Mirrors firmware `cfgSetDefaultCfg()`.
 *
 * @param programNumber  The drying program number (0–5).
 * @param sensorTypes    Current sensor types per slot (from comBoxStatus).
 * @param totalSlots     Total number of sensor slots (5 or 9).
 * @param sensorSerials  Optional map of sensor index → serial number (for short sensor detection).
 * @param isMini         Whether the device is a MINI (affects default short/long when serial is unknown).
 * @returns              New sensorUsage array with program-appropriate defaults.
 */
export function getDefaultSensorConfig(
  programNumber: number,
  sensorTypes: ComBoxSensorType[],
  totalSlots: number,
  sensorSerials?: Map<number, string>,
  isMini?: boolean,
): ComBoxSensorUsage[] {
  const rules = getRules(programNumber);
  const usage: ComBoxSensorUsage[] = new Array(totalSlots).fill(
    ComBoxSensorUsage.NOT_USED,
  );

  // Set room sensor default
  if (
    ROOM_SENSOR_INDEX < totalSlots &&
    isSlotConnected(sensorTypes[ROOM_SENSOR_INDEX])
  ) {
    usage[ROOM_SENSOR_INDEX] = rules.roomDefault;
  }

  // Set external sensor defaults
  for (let i = 0; i < totalSlots; i++) {
    if (i === ROOM_SENSOR_INDEX) continue;
    if (isSlotConnected(sensorTypes[i])) {
      usage[i] = rules.extDefault;
    }
  }

  // P2 exception: with ≥2 usable ext, room=MESS, 1st ext=REF, 2nd ext=PROG
  if (rules.autoAssignOnSecondExt) {
    const usableExt = countUsableExtSensors(sensorTypes);
    if (usableExt >= 2) {
      usage[ROOM_SENSOR_INDEX] = ComBoxSensorUsage.MEASUREMENT;
      let extCount = 0;
      for (let i = 0; i < totalSlots; i++) {
        if (i === ROOM_SENSOR_INDEX) continue;
        if (!isSlotConnected(sensorTypes[i])) continue;
        extCount++;
        if (extCount === 1) {
          usage[i] = ComBoxSensorUsage.REFERENCE;
        } else if (extCount === 2) {
          usage[i] = ComBoxSensorUsage.PROGRAM;
        }
        // rest keep extDefault (PROG)
      }
    }
  }

  // Room→REF promotion when ≤1 usable ext (mirrors validate Step 6)
  if (rules.roomIsDefaultRef) {
    const usableExt = countUsableExtSensors(sensorTypes);
    if (usableExt <= 1 && isSlotConnected(sensorTypes[ROOM_SENSOR_INDEX])) {
      usage[ROOM_SENSOR_INDEX] = ComBoxSensorUsage.REFERENCE;
    }
  }

  // P5 exception: when ext sensors exist, room becomes MESS
  if (programNumber === 5) {
    const usableExt = countUsableExtSensors(sensorTypes);
    if (usableExt > 0) {
      usage[ROOM_SENSOR_INDEX] = ComBoxSensorUsage.MEASUREMENT;
    }
  }

  // Apply short sensor restriction after defaults
  if (isShortSensorRestrictionActive(programNumber, usage[ROOM_SENSOR_INDEX])) {
    for (let i = 0; i < totalSlots; i++) {
      if (i === ROOM_SENSOR_INDEX) continue;
      if (!isSlotConnected(sensorTypes[i])) continue;
      if (isShortSensor(sensorTypes[i], sensorSerials?.get(i), isMini)) {
        usage[i] = ComBoxSensorUsage.MEASUREMENT;
      }
    }
  }

  enforceMeasurementOnlySensors(usage, sensorTypes);

  return usage;
}

// ---------------------------------------------------------------------------
// validateSensorConfig — mirrors firmware cfgValidateSensorCfg()
// ---------------------------------------------------------------------------

/**
 * Validates and corrects a sensor configuration against program rules.
 * Does NOT reset to full defaults — preserves existing assignments where
 * possible, only fixing rule violations.
 *
 * Called after:
 * - getDefaultSensorConfig() (safety net)
 * - switchSensorConfig() (post-switch correction)
 * - collectControlConfig() (final guard before sending)
 * - START dialog open (after defaults)
 *
 * Mirrors firmware `cfgValidateSensorCfg()`.
 *
 * @param sensorUsage           Current sensor usage array.
 * @param programNumber         The drying program number (0–5).
 * @param needsReferenceSensor  Whether the program requires a REF sensor.
 * @param sensorTypes           Current sensor types per slot (from comBoxStatus).
 * @param sensorSerials         Optional map of sensor index → serial number.
 * @param isMini                Whether the device is a MINI (affects default short/long when serial is unknown).
 * @returns                     Corrected sensorUsage array (new array, does not mutate input).
 */
export function validateSensorConfig(
  sensorUsage: ComBoxSensorUsage[],
  programNumber: number,
  needsReferenceSensor: boolean,
  sensorTypes: ComBoxSensorType[],
  sensorSerials?: Map<number, string>,
  isMini?: boolean,
): ComBoxSensorUsage[] {
  const rules = getRules(programNumber);
  const usage = [...sensorUsage];

  // Step 1: Force NOT_USED for empty/unknown sensor slots
  for (let i = 0; i < usage.length; i++) {
    if (!isSlotConnected(sensorTypes[i])) {
      usage[i] = ComBoxSensorUsage.NOT_USED;
    }
  }

  // Step 2: P3 (Monitor) — force all connected sensors to MESS, return early
  if (programNumber === 3) {
    for (let i = 0; i < usage.length; i++) {
      if (isSlotConnected(sensorTypes[i])) {
        usage[i] = ComBoxSensorUsage.MEASUREMENT;
      }
    }

    enforceMeasurementOnlySensors(usage, sensorTypes);
    return usage;
  }

  // Step 3: P4 — force room=PROG
  if (programNumber === 4) {
    if (isSlotConnected(sensorTypes[ROOM_SENSOR_INDEX])) {
      usage[ROOM_SENSOR_INDEX] = ComBoxSensorUsage.PROGRAM;
    }
  }

  // Step 4: P5 — if no usable ext sensors, force room=PROG
  if (programNumber === 5) {
    const usableExt = countUsableExtSensors(sensorTypes);
    if (usableExt === 0) {
      if (isSlotConnected(sensorTypes[ROOM_SENSOR_INDEX])) {
        usage[ROOM_SENSOR_INDEX] = ComBoxSensorUsage.PROGRAM;
      }
    }
  }

  // Step 5: Block external REF if program doesn't allow it
  if (!rules.extCanBeRef) {
    for (let i = 0; i < usage.length; i++) {
      if (i === ROOM_SENSOR_INDEX) continue;
      if (usage[i] === ComBoxSensorUsage.REFERENCE) {
        usage[i] = ComBoxSensorUsage.MEASUREMENT;
      }
    }
  }

  // Step 6: Room→REF auto-promotion when ≤1 usable ext
  if (rules.roomIsDefaultRef) {
    const usableExt = countUsableExtSensors(sensorTypes);
    if (usableExt <= 1 && isSlotConnected(sensorTypes[ROOM_SENSOR_INDEX])) {
      usage[ROOM_SENSOR_INDEX] = ComBoxSensorUsage.REFERENCE;
    }
  }

  // Step 7: Ensure exactly one REF if needed
  if (needsReferenceSensor && rules.autoCreateRef) {
    let refCount = 0;
    for (let i = 0; i < usage.length; i++) {
      if (usage[i] === ComBoxSensorUsage.REFERENCE) refCount++;
    }

    if (refCount === 0) {
      // No REF found — set room to REF
      if (isSlotConnected(sensorTypes[ROOM_SENSOR_INDEX])) {
        usage[ROOM_SENSOR_INDEX] = ComBoxSensorUsage.REFERENCE;
      }
    } else if (refCount > 1) {
      // Multiple REFs — keep first found (room checked first), demote rest to MESS
      let firstFound = false;
      if (usage[ROOM_SENSOR_INDEX] === ComBoxSensorUsage.REFERENCE) {
        firstFound = true;
      }
      for (let i = 0; i < usage.length; i++) {
        if (i === ROOM_SENSOR_INDEX) continue;
        if (usage[i] === ComBoxSensorUsage.REFERENCE) {
          if (!firstFound) {
            firstFound = true;
          } else {
            usage[i] = ComBoxSensorUsage.MEASUREMENT;
          }
        }
      }
    }
  }

  // Step 8: Ensure ≥1 PROG if autoCreateProg
  if (rules.autoCreateProg) {
    const hasProgSensor = usage.some((u) => u === ComBoxSensorUsage.PROGRAM);
    if (!hasProgSensor) {
      // Promote first eligible external sensor to PROG
      for (let i = 0; i < usage.length; i++) {
        if (i === ROOM_SENSOR_INDEX) continue;
        if (!isSlotConnected(sensorTypes[i])) continue;
        if (usage[i] === ComBoxSensorUsage.REFERENCE) continue;
        // Skip short sensors when short restriction is active
        if (
          isShortSensorRestrictionActive(programNumber, usage[ROOM_SENSOR_INDEX]) &&
          isShortSensor(sensorTypes[i], sensorSerials?.get(i), isMini)
        ) {
          continue;
        }
        usage[i] = ComBoxSensorUsage.PROGRAM;
        break;
      }
    }
  }

  // Step 9: Short sensor restriction (P0/P1/P2 + room=REF)
  if (isShortSensorRestrictionActive(programNumber, usage[ROOM_SENSOR_INDEX])) {
    for (let i = 0; i < usage.length; i++) {
      if (i === ROOM_SENSOR_INDEX) continue;
      if (!isSlotConnected(sensorTypes[i])) continue;
      if (isShortSensor(sensorTypes[i], sensorSerials?.get(i), isMini)) {
        usage[i] = ComBoxSensorUsage.MEASUREMENT;
      }
    }
  }

  enforceMeasurementOnlySensors(usage, sensorTypes);

  return usage;
}

// ---------------------------------------------------------------------------
// switchSensorConfig — mirrors firmware cfgSwitchSensorCfg()
// ---------------------------------------------------------------------------

/**
 * Determines whether a sensor at the given index can be toggled.
 * Used by SensorStatus.vue to disable the toggle button visually.
 *
 * @returns true if the sensor can be switched, false if the click should be refused.
 */
export function canToggleSensor(
  sensorIndex: number,
  sensorUsage: ComBoxSensorUsage[],
  programNumber: number,
  needsReferenceSensor: boolean,
  sensorTypes: ComBoxSensorType[],
  sensorSerials?: Map<number, string>,
  isMini?: boolean,
): boolean {
  const currentUsage = sensorUsage[sensorIndex];
  const isRoomSensor = sensorIndex === ROOM_SENSOR_INDEX;

  // BLE type 3/material and BLE type 5 sensors are hard-locked to MEASUREMENT.
  if (isMeasurementOnlySensor(sensorTypes[sensorIndex])) {
    return false;
  }

  // REF sensors cannot be switched
  if (currentUsage === ComBoxSensorUsage.REFERENCE) {
    return false;
  }

  // P3: no switching allowed
  if (programNumber === 3) {
    return false;
  }

  // P4: room sensor always PROG, cannot be switched
  if (programNumber === 4 && isRoomSensor) {
    return false;
  }

  // P5: room sensor locked if it's PROG and no ext PROG candidates exist
  if (programNumber === 5 && isRoomSensor && currentUsage === ComBoxSensorUsage.PROGRAM) {
    let extProgCount = 0;
    for (let i = 0; i < sensorUsage.length; i++) {
      if (i === ROOM_SENSOR_INDEX) continue;
      if (!isSlotConnected(sensorTypes[i])) continue;
      if (sensorUsage[i] === ComBoxSensorUsage.PROGRAM) extProgCount++;
    }
    if (extProgCount === 0) {
      return false;
    }
  }

  // Short sensor restriction: P0/P1/P2 + room=REF + ext sensor is short
  if (
    !isRoomSensor &&
    isShortSensorRestrictionActive(programNumber, sensorUsage[ROOM_SENSOR_INDEX]) &&
    isShortSensor(sensorTypes[sensorIndex], sensorSerials?.get(sensorIndex), isMini)
  ) {
    return false;
  }

  return true;
}

/**
 * Performs a sensor usage toggle at the given index, then runs post-switch
 * validation. Returns a new sensorUsage array.
 *
 * If the sensor cannot be toggled (per refusal rules), returns the original
 * array unchanged.
 *
 * Mirrors firmware `cfgSwitchSensorCfg()`.
 *
 * @param sensorIndex           Index of the sensor to toggle.
 * @param sensorUsage           Current sensor usage array.
 * @param programNumber         The drying program number (0–5).
 * @param needsReferenceSensor  Whether the program requires a REF sensor.
 * @param sensorTypes           Current sensor types per slot.
 * @param sensorSerials         Optional map of sensor index → serial number.
 * @param isMini                Whether the device is a MINI (affects default short/long when serial is unknown).
 * @returns                     New sensorUsage array after switching + validation.
 */
export function switchSensorConfig(
  sensorIndex: number,
  sensorUsage: ComBoxSensorUsage[],
  programNumber: number,
  needsReferenceSensor: boolean,
  sensorTypes: ComBoxSensorType[],
  sensorSerials?: Map<number, string>,
  isMini?: boolean,
): ComBoxSensorUsage[] {
  // Check refusal rules
  if (
    !canToggleSensor(
      sensorIndex,
      sensorUsage,
      programNumber,
      needsReferenceSensor,
      sensorTypes,
      sensorSerials,
      isMini,
    )
  ) {
    return sensorUsage;
  }

  const usage = [...sensorUsage];
  const currentUsage = usage[sensorIndex];
  const isRoomSensor = sensorIndex === ROOM_SENSOR_INDEX;

  // Determine the new usage via cycling logic
  let newUsage: ComBoxSensorUsage;

  if (isRoomSensor) {
    if (needsReferenceSensor) {
      // Room + needsRef: MESS → REF → MESS (skip PROG)
      if (currentUsage === ComBoxSensorUsage.MEASUREMENT) {
        newUsage = ComBoxSensorUsage.REFERENCE;
      } else {
        newUsage = ComBoxSensorUsage.MEASUREMENT;
      }
    } else {
      // Room + no ref: MESS → PROG → MESS
      if (currentUsage === ComBoxSensorUsage.MEASUREMENT) {
        newUsage = ComBoxSensorUsage.PROGRAM;
      } else {
        newUsage = ComBoxSensorUsage.MEASUREMENT;
      }
    }
  } else {
    // External sensor
    if (needsReferenceSensor) {
      // Ext + needsRef: MESS → PROG → REF → MESS
      if (currentUsage === ComBoxSensorUsage.MEASUREMENT) {
        newUsage = ComBoxSensorUsage.PROGRAM;
      } else if (currentUsage === ComBoxSensorUsage.PROGRAM) {
        newUsage = ComBoxSensorUsage.REFERENCE;
      } else {
        newUsage = ComBoxSensorUsage.MEASUREMENT;
      }
    } else {
      // Ext + no ref: MESS → PROG → MESS
      if (currentUsage === ComBoxSensorUsage.MEASUREMENT) {
        newUsage = ComBoxSensorUsage.PROGRAM;
      } else {
        newUsage = ComBoxSensorUsage.MEASUREMENT;
      }
    }
  }

  // If switching to REF, clear all existing REF sensors first
  if (newUsage === ComBoxSensorUsage.REFERENCE) {
    for (let i = 0; i < usage.length; i++) {
      if (usage[i] === ComBoxSensorUsage.REFERENCE) {
        usage[i] = ComBoxSensorUsage.MEASUREMENT;
      }
    }
  }

  usage[sensorIndex] = newUsage;

  // Post-switch validation
  return validateSensorConfig(
    usage,
    programNumber,
    needsReferenceSensor,
    sensorTypes,
    sensorSerials,
    isMini,
  );
}
